import { useEffect, useMemo, useState } from 'react';
import { ShoppingCart, Receipt, CircleCheck, Trash2, TriangleAlert, Package, BedDouble, Search } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { ApiError } from '@/lib/api/client';
import { useBillingSettings } from '@/hooks/useBillingSettings';
import { openBillPdf } from '@/features/billing/api';
import { PrintBillButton } from '@/features/printing/PrintBillButton';
import { ScheduleBadge } from '../ScheduleBadge';
import { useInventory } from '../useInventory';
import { useDispensingSettings } from '../useNearExpiry';
import { NearExpiryBadge, LaterBatchPanel, batchSelectionSummary } from '../BatchSelectionPanel';
import { pickFefoBatch, unresolvedLaterBatches, applyBatchSelectionReason, isNearExpiry } from '../batchSelection';
import { Empty, QtyStepper, StepRail, TotalLine, PaymentModePicker, PaymentStatusPicker, money, type Step } from '../outpatient/ui';
import { useAdmissionSearch, useAdmissionActiveOrders, useCreateIpPharmacySale } from './useIpPharmacy';
import type { AdmissionSearchResult, IpPharmacySaleResult } from './api';
import type { DrugSchedule, InventoryItem, PaymentMode } from '../types';

const isExpired = (d: string | null | undefined) => !!d && new Date(d) < new Date();
const SCHEDULED: DrugSchedule[] = ['H', 'H1', 'X'];

interface CartLine {
  inventory_id: number;
  medicine_id: number;
  name: string;
  qty: number;
  mrp: number;
  gst: number;
  discount: number;
  schedule: DrugSchedule;
  batch: string;
  expiry: string;
  available: number;
  batch_selection_reason?: string;
  batch_selection_remarks?: string;
}

/**
 * Who this purchase is for: an active admission, looked up by bed/ward/
 * patient rather than asking the attender for a UHID they may not be
 * carrying. Deliberately narrow (only `admitted` stays returned) — see
 * routes/ip-pharmacy.js.
 */
function AdmissionPanel({ admission, onSelect, onClear }: {
  admission: AdmissionSearchResult | null;
  onSelect: (a: AdmissionSearchResult) => void;
  onClear: () => void;
}) {
  const [q, setQ] = useState('');
  const search = useAdmissionSearch(q);
  const results = search.data ?? [];

  if (admission) {
    return (
      <Card>
        <CardHeader>
          <CardTitle><BedDouble size={15} /> {admission.patient_name}</CardTitle>
          <Button size="sm" variant="ghost" onClick={onClear}>Change</Button>
        </CardHeader>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink-muted">
          <span className="font-mono">{admission.uhid}</span>
          <span>{admission.ward_name} · Bed {admission.bed_number}</span>
          {admission.mobile && <span>{admission.mobile}</span>}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle><Search size={15} /> Find the admission</CardTitle>
      </CardHeader>
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Patient name, UHID, mobile or bed number"
        autoFocus
      />
      <div className="mt-2 space-y-1.5">
        {q.trim().length >= 2 && results.length === 0 && !search.isFetching && (
          <Empty label="No admitted patient matches this" />
        )}
        {results.map((a) => (
          <button
            key={a.admission_id}
            type="button"
            disabled={a.deceased}
            title={a.deceased ? 'Recorded as deceased — a new sale cannot be made on this record' : undefined}
            onClick={() => onSelect(a)}
            className="flex w-full items-center gap-3 rounded-lg border border-border px-3 py-2.5 text-left transition-colors hover:border-teal hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="grid h-8 w-8 shrink-0 place-content-center rounded-full bg-muted text-ink-muted">
              <BedDouble size={15} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold">{a.patient_name}</span>
              <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-ink-muted">
                <span className="font-mono">{a.uhid}</span>
                <span>{a.ward_name} · Bed {a.bed_number}</span>
              </span>
            </span>
          </button>
        ))}
      </div>
    </Card>
  );
}

/**
 * The IP Pharmacy counter — an attender (or staff) buying medicine against an
 * ACTIVE ADMISSION, so insurance gets an itemized, admission-linked bill per
 * purchase instead of the one-line lump-sum ip_charges workaround this
 * replaces. See routes/ip-pharmacy.js's own header for the full reasoning.
 *
 * Deliberately simpler than the walk-in counter (OutpatientTab): no loyalty
 * (not an outpatient concept), no outside-prescription capture (just a plain
 * prescriber-name field, since Schedule H/H1/X is the only thing here that
 * ever needs one) — but otherwise the SAME cart, batch and GST mechanics,
 * because it sells through the same engine.
 */
export default function IpPharmacyTab() {
  const { gstEnabled, gstPercentageOptions } = useBillingSettings();
  const { near_expiry_days: nearExpiryDays, batch_selection_reasons: batchReasons } = useDispensingSettings();

  const [admission, setAdmission] = useState<AdmissionSearchResult | null>(null);
  const [items, setItems] = useState<CartLine[]>([]);
  const [prescriberName, setPrescriberName] = useState('');
  const [payMode, setPayMode] = useState<PaymentMode>('Cash');
  // Settles at discharge by default — an IP account, not a walk-in till.
  const [payStatus, setPayStatus] = useState<'Paid' | 'Pending'>('Pending');
  const [formError, setFormError] = useState('');
  const [confirmed, setConfirmed] = useState<IpPharmacySaleResult | null>(null);
  const [advancedBatch, setAdvancedBatch] = useState(false);

  const inventoryQuery = useInventory({ channel: 'admission' });
  const activeOrdersQuery = useAdmissionActiveOrders(admission?.admission_id ?? null);
  const createSale = useCreateIpPharmacySale();

  const inventory = inventoryQuery.data ?? [];
  const activeOrders = activeOrdersQuery.data ?? [];

  const gross = items.reduce((s, i) => s + i.qty * i.mrp, 0);
  const discount = items.reduce((s, i) => s + (i.qty * i.mrp * i.discount) / 100, 0);
  const net = gross - discount;
  const gstAmt = gstEnabled
    ? items.reduce((s, i) => s + (i.qty * i.mrp * (1 - i.discount / 100) * i.gst) / 100, 0)
    : 0;
  const total = net + gstAmt;

  const cartHasScheduled = items.some((i) => SCHEDULED.includes(i.schedule));
  const prescriptionBlocks = cartHasScheduled && !prescriberName.trim();

  const dispensable = useMemo(
    () => inventory.filter((m) => m.quantity_available > 0 && !isExpired(m.expiry_date)),
    [inventory]
  );
  const fefoGroups = useMemo(() => {
    const groups: Record<number, InventoryItem> = {};
    dispensable.forEach((m) => {
      if (!groups[m.medicine_id] || new Date(m.expiry_date) < new Date(groups[m.medicine_id]!.expiry_date)) {
        groups[m.medicine_id] = m;
      }
    });
    return groups;
  }, [dispensable]);

  const addFromInventory = (invId: string, qty = 1, fefo = false) => {
    const picked = inventory.find((m) => m.id === parseInt(invId, 10));
    if (!picked) return;
    const med = fefo
      ? pickFefoBatch(dispensable.filter((m) => m.medicine_id === picked.medicine_id), qty, items) ?? picked
      : picked;
    const medGst = parseFloat(med.gst_percent) || 12;
    const validGst = gstPercentageOptions.includes(medGst) ? medGst : (gstPercentageOptions[0] ?? 0);
    setItems((prev) => {
      const existing = prev.findIndex((l) => l.inventory_id === med.id);
      if (existing >= 0) {
        return prev.map((l, idx) => (idx === existing ? { ...l, qty: Math.min(l.qty + qty, l.available) } : l));
      }
      return [...prev, {
        inventory_id: med.id,
        medicine_id: med.medicine_id,
        name: `${med.generic_name} ${med.strength || ''}`.trim(),
        qty: Math.min(qty, med.quantity_available) || 1,
        mrp: parseFloat(med.mrp),
        gst: gstEnabled ? validGst : 0,
        discount: 0,
        schedule: med.schedule,
        batch: med.batch_number,
        expiry: med.expiry_date,
        available: med.quantity_available,
      }];
    });
  };

  const unresolvedLater = useMemo(() => unresolvedLaterBatches(items, dispensable), [items, dispensable]);
  const applyLaterBatchReason = (ids: Set<number>, reason: string, remarks: string) =>
    setItems((prev) => applyBatchSelectionReason(prev, ids, reason, remarks));
  const switchToEarlierBatch = (line: CartLine, earlierId: number) => {
    const earlier = inventory.find((m) => m.id === earlierId);
    if (!earlier) return;
    setItems((prev) => {
      const existing = prev.find((l) => l.inventory_id === earlier.id);
      if (existing) {
        return prev.filter((l) => l !== line).map((l) => (l === existing ? { ...l, qty: Math.min(l.qty + line.qty, l.available) } : l));
      }
      return prev.map((l) => (l === line
        ? { ...l, inventory_id: earlier.id, batch: earlier.batch_number, expiry: earlier.expiry_date, available: earlier.quantity_available, mrp: parseFloat(earlier.mrp) }
        : l));
    });
  };
  const updateLine = (idx: number, patch: Partial<CartLine>) =>
    setItems((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  useEffect(() => {
    const options = gstPercentageOptions.map(String);
    if (!options.length) return;
    setItems((prev) => prev.map((l) => (options.includes(String(l.gst)) ? l : { ...l, gst: parseFloat(options[0] as string) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gstPercentageOptions]);

  const reset = () => {
    setAdmission(null);
    setItems([]);
    setPrescriberName('');
    setPayMode('Cash');
    setPayStatus('Pending');
    setFormError('');
  };

  const save = () => {
    if (!admission || !items.length) return;
    if (prescriptionBlocks) {
      setFormError("This cart contains a Schedule H/H1/X medicine — enter the prescriber's name first.");
      return;
    }
    if (unresolvedLater.length) {
      setFormError('A later-expiry batch is in the cart while an earlier one is available — give a reason or switch batch first.');
      return;
    }
    setFormError('');
    createSale.mutate(
      {
        admissionId: admission.admission_id,
        input: {
          items: items.map((i) => ({
            inventory_id: i.inventory_id,
            name: i.name,
            quantity: i.qty,
            gst_percent: gstEnabled ? i.gst : 0,
            discount_percent: i.discount || undefined,
            batch_selection_reason: i.batch_selection_reason,
            batch_selection_remarks: i.batch_selection_remarks,
          })),
          prescriber_name: prescriberName || undefined,
          payment_mode: payMode,
          payment_status: payStatus,
        },
      },
      { onSuccess: (sale) => { setConfirmed(sale); reset(); } }
    );
  };

  const saleError = createSale.error instanceof ApiError ? createSale.error.message : null;

  const steps: Step[] = [
    { id: 'admission', label: 'Admission', state: admission ? 'done' : 'active' },
    { id: 'cart', label: 'Medicines', state: items.length ? 'done' : admission ? 'active' : 'todo' },
    { id: 'pay', label: 'Payment', state: items.length ? 'active' : 'todo' },
  ];

  return (
    <div className="space-y-4">
      <StepRail steps={steps} />

      {confirmed && (
        <Card className="border-success-soft">
          <div className="flex flex-wrap items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-content-center rounded-full bg-success-soft text-success-ink">
              <CircleCheck size={20} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">
                Bill {confirmed.bill.bill_number} · {money(confirmed.bill.total_amount)}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <Badge variant={confirmed.bill.payment_status === 'Paid' ? 'success' : 'warn'}>
                  {confirmed.bill.payment_status === 'Paid' ? 'Paid' : 'Pending — settles at discharge'}
                </Badge>
              </div>
              {confirmed.quantity_flags.filter((f) => f.exceeds).map((f) => (
                <div key={f.order_id} className="mt-1.5 rounded bg-warn-soft px-2.5 py-1.5 text-[11.5px] text-caution-ink">
                  ⚠ {f.drug_name}: {f.total_after} dispensed so far exceeds the ~{f.implied_quantity} the order's schedule implies — advisory only.
                </div>
              ))}
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button size="sm" variant="ghost" onClick={() => { void openBillPdf(confirmed.bill.id); }}>
                <Receipt size={13} /> Invoice
              </Button>
              <PrintBillButton billId={confirmed.bill.id} />
              <Button size="sm" onClick={() => setConfirmed(null)}>Next sale</Button>
            </div>
          </div>
        </Card>
      )}

      {!admission ? (
        <div className="mx-auto w-full max-w-xl">
          <AdmissionPanel admission={null} onSelect={(a) => { setAdmission(a); setConfirmed(null); }} onClear={reset} />
        </div>
      ) : (
        <div className="grid items-start gap-4 min-[1100px]:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
          <div className="space-y-4">
            <AdmissionPanel admission={admission} onSelect={() => {}} onClear={reset} />

            {activeOrders.length > 0 && (
              <Card>
                <CardHeader><CardTitle>Active medication orders</CardTitle></CardHeader>
                <p className="-mt-2 mb-2 text-[11.5px] text-ink-muted">
                  Informational only — adding one of these drugs below is not required to match an order.
                </p>
                <ul className="space-y-1 text-[12.5px]">
                  {activeOrders.map((o) => (
                    <li key={o.id} className="flex items-center gap-1.5">
                      <span className="font-medium">{o.drug_name}</span>
                      {o.strength && <span className="text-ink-muted">{o.strength}</span>}
                      <Badge variant={o.status === 'held' ? 'warn' : 'neutral'}>{o.status}</Badge>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>

          <div className="space-y-4 min-[1100px]:sticky min-[1100px]:top-4">
            <Card>
              <CardHeader>
                <CardTitle><ShoppingCart size={15} /> Medicines</CardTitle>
                {items.length > 0 && <Badge variant="neutral">{items.length} line{items.length === 1 ? '' : 's'}</Badge>}
              </CardHeader>

              {(saleError || formError) && (
                <div className="mb-3 flex gap-2.5 rounded-lg bg-danger-soft px-3 py-2.5 text-[12px] text-danger-ink">
                  <TriangleAlert size={15} className="mt-px shrink-0" />
                  <p className="leading-relaxed">{saleError || formError}</p>
                </div>
              )}

              <NativeSelect
                defaultValue=""
                aria-label="Add a medicine"
                onChange={(e) => { if (e.target.value) { addFromInventory(e.target.value, 1, true); e.target.value = ''; } }}
              >
                <option value="">+ Add a medicine…</option>
                {Object.values(fefoGroups).map((m) => (
                  <option key={m.medicine_id} value={m.id}>
                    {isNearExpiry(m.expiry_date, nearExpiryDays) ? '⏳ ' : ''}{m.generic_name} {m.strength} {m.schedule !== 'OTC' ? `[${m.schedule}]` : ''} — ₹{parseFloat(m.mrp).toFixed(2)} · {m.quantity_available} left
                  </option>
                ))}
              </NativeSelect>
              <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
                <p className="text-[11px] text-ink-muted">
                  Earliest-expiring batch first. ⏳ = expires within {nearExpiryDays} days.
                </p>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => setAdvancedBatch((a) => !a)}>
                  {advancedBatch ? '▲ Hide' : '▼'} Pick a specific batch
                </Button>
              </div>
              {advancedBatch && (
                <NativeSelect
                  className="mt-1.5"
                  defaultValue=""
                  aria-label="Add a specific batch"
                  onChange={(e) => { if (e.target.value) { addFromInventory(e.target.value); e.target.value = ''; } }}
                >
                  <option value="">Select exact batch… (a later batch needs a reason)</option>
                  {dispensable.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.generic_name} {m.strength} — {m.batch_number} (exp {new Date(m.expiry_date).toLocaleDateString('en-IN')}, {m.quantity_available} left)
                    </option>
                  ))}
                </NativeSelect>
              )}

              <div className="mt-3.5">
                {items.length === 0 ? (
                  <Empty label="Nothing in the cart yet" hint="Add a medicine above." />
                ) : (
                  <div className="divide-y divide-line">
                    {items.map((item, idx) => {
                      const lineGross = item.qty * item.mrp;
                      const lineNet = lineGross * (1 - item.discount / 100);
                      const lowStock = item.qty >= item.available;
                      return (
                        <div key={item.inventory_id} className="py-2.5 first:pt-0">
                          <div className="flex items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <span className="truncate text-[13px] font-semibold">{item.name}</span>
                                {item.schedule !== 'OTC' && <ScheduleBadge schedule={item.schedule} />}
                                <NearExpiryBadge expiry={item.expiry} nearExpiryDays={nearExpiryDays} />
                                {item.batch_selection_reason && (
                                  <Badge variant="warn" title={`Later-expiry batch: ${batchSelectionSummary(item, batchReasons)}`}>↷ Later batch</Badge>
                                )}
                              </div>
                              <div className="mt-0.5 text-[11px] text-ink-muted">
                                {money(item.mrp)} each · batch {item.batch}
                                {item.batch_selection_reason && <> · {batchSelectionSummary(item, batchReasons)}</>}
                              </div>
                            </div>
                            <div className="shrink-0 text-right">
                              <div className="text-[13px] font-semibold tabular-nums">{money(lineNet)}</div>
                              {item.discount > 0 && <div className="text-[11px] text-ink-muted line-through tabular-nums">{money(lineGross)}</div>}
                            </div>
                            <button
                              type="button"
                              aria-label={`Remove ${item.name}`}
                              onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                              className="mt-px shrink-0 rounded-md p-1 text-ink-muted transition-colors hover:bg-danger-soft hover:text-danger-ink"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>

                          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
                            <QtyStepper value={item.qty} max={item.available} onChange={(v) => updateLine(idx, { qty: v })} />
                            <label className="flex items-center gap-1.5">
                              <span className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Disc</span>
                              <div className="relative">
                                <Input
                                  type="number"
                                  className="h-7 w-16 pr-5 text-[13px] tabular-nums"
                                  value={item.discount}
                                  min={0}
                                  max={100}
                                  onChange={(e) => updateLine(idx, { discount: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) })}
                                />
                                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-ink-muted">%</span>
                              </div>
                            </label>
                            {gstEnabled && (
                              <label className="flex items-center gap-1.5">
                                <span className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">GST</span>
                                <NativeSelect
                                  className="h-7 w-[74px] py-0 text-[13px]"
                                  value={item.gst}
                                  onChange={(e) => updateLine(idx, { gst: parseFloat(e.target.value) || 0 })}
                                >
                                  {gstPercentageOptions.map((g) => <option key={g} value={g}>{g}%</option>)}
                                </NativeSelect>
                              </label>
                            )}
                            {lowStock && (
                              <Badge variant="warn" className="inline-flex items-center gap-1">
                                <Package size={10} /> All {item.available} taken
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <LaterBatchPanel
                className="mt-3"
                issues={unresolvedLater}
                reasons={batchReasons}
                onApply={applyLaterBatchReason}
                onUseEarlier={switchToEarlierBatch}
              />
            </Card>

            {items.length > 0 && (
              <Card>
                <div className="rounded-lg bg-canvas p-3">
                  <TotalLine label="Subtotal" value={gross} />
                  {discount > 0 && <TotalLine label="Discount" value={discount} tone="credit" />}
                  {gstEnabled && <TotalLine label="GST" value={gstAmt} hint="on the discounted value" />}
                  <TotalLine label="To pay" value={total} tone="grand" />
                </div>

                {cartHasScheduled && (
                  <div className="mt-3.5">
                    <label className="mb-1 block text-xs font-semibold">
                      Prescriber name <span className="font-normal text-ink-muted">(required — Schedule H/H1/X)</span>
                    </label>
                    <Input value={prescriberName} onChange={(e) => setPrescriberName(e.target.value)} />
                  </div>
                )}

                <div className="mt-3.5 space-y-3">
                  <PaymentModePicker value={payMode} onChange={setPayMode} />
                  <PaymentStatusPicker
                    value={payStatus}
                    onChange={setPayStatus}
                    paidHint="Money taken now"
                    pendingHint="Added to the admission's account — settled at discharge"
                  />
                </div>

                {admission.deceased && (
                  <div className="mt-3 flex gap-2.5 rounded-lg bg-danger-soft px-3 py-2.5 text-[12px] text-danger-ink">
                    <TriangleAlert size={15} className="mt-px shrink-0" />
                    <p className="leading-relaxed">{admission.patient_name} is recorded as deceased — a new sale cannot be made on this record.</p>
                  </div>
                )}

                <Button
                  variant="success"
                  full
                  size="lg"
                  className="mt-3.5"
                  disabled={createSale.isPending || items.length === 0 || prescriptionBlocks || unresolvedLater.length > 0 || admission.deceased}
                  loading={createSale.isPending}
                  onClick={save}
                >
                  {createSale.isPending ? 'Saving…'
                    : admission.deceased ? 'Sale blocked — patient recorded as deceased'
                    : unresolvedLater.length > 0 ? 'Resolve batch choice to continue'
                    : `Bill · ${money(total)}`}
                </Button>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
